import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  Grid,
  Paper,
  Container,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import StyledButton from '../../common/StyledButton';
import { useAuth } from '../../../context/AuthContext';

// Single shared Stripe instance for the whole module. The previous
// implementation also re-loaded Stripe inside PaymentStep; that was wasteful
// and meant we held two distinct stripe.js promises in flight simultaneously.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const POLL_TIMEOUT_MS = 30_000;
const POLL_INTERVAL_MS = 1_500;

// Wait for the webhook to mark our payment row succeeded. The webhook is now
// the single source of truth for finalizing registration; the client just
// reflects whatever it has done.
const waitForServerConfirmation = async (paymentIntentId) => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
    try {
      const response = await fetch(`/api/payment/status/${paymentIntentId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'succeeded') return data;
      }
    } catch (err) {
      // Network blips are fine; we just retry until the timeout.
    }
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
  return null;
};

const PaymentForm = ({ onSuccess, onError, onRestart, amount, expiresAt }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(null);

  const isExpired = timeLeft !== null && timeLeft <= 0;

  useEffect(() => {
    if (!expiresAt) return;
    const updateTimer = () => {
      const now = Math.floor(Date.now() / 1000);
      const remaining = expiresAt - now;
      setTimeLeft(remaining > 0 ? remaining : 0);
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setStatusMessage('');

    if (!stripe || !elements) {
      setLoading(false);
      return;
    }

    try {
      const { error: submitError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/vendor/confirmation`,
        },
        redirect: 'if_required',
      });

      if (submitError) {
        let errorMessage = submitError.message;
        switch (submitError.code) {
          case 'card_declined':
            errorMessage =
              'Your card was declined. Please try a different card or contact your bank.';
            break;
          case 'expired_card':
            errorMessage = 'Your card has expired. Please use a different card.';
            break;
          case 'incorrect_cvc':
            errorMessage =
              'The security code (CVC) is incorrect. Please check and try again.';
            break;
          case 'processing_error':
            errorMessage =
              'There was an error processing your card. Please try again in a few moments.';
            break;
          default:
            errorMessage = submitError.message;
        }
        setError(errorMessage);
        if (onError) onError(errorMessage);
        return;
      }

      if (paymentIntent?.status !== 'succeeded') {
        const fallback = 'Payment was not completed. Please try again.';
        setError(fallback);
        if (onError) onError(fallback);
        return;
      }

      setStatusMessage(
        "Payment received. Finalizing your registration — this usually takes a few seconds…"
      );

      const confirmed = await waitForServerConfirmation(paymentIntent.id);
      if (!confirmed) {
        const message =
          "Your payment went through but we couldn't confirm finalization in time. " +
          "Refresh the page in a minute; your dashboard should reflect the payment. " +
          "If not, contact the festival organizers with your confirmation number.";
        setError(message);
        if (onError) onError(message);
        return;
      }

      if (onSuccess) {
        onSuccess({
          status: 'succeeded',
          paymentIntent: paymentIntent.id,
          message: 'Payment successful and registration completed!',
        });
      }
    } catch (err) {
      console.error('Payment error:', err);
      const fallback =
        'An unexpected error occurred. Please try again or contact support if the problem persists.';
      setError(fallback);
      if (onError) onError(fallback);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeLeft = (seconds) => {
    if (seconds <= 0) return 'Expired';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const calculateProgress = () => {
    if (!expiresAt) return 100;
    const totalTime = 3600;
    const elapsed = totalTime - (timeLeft || 0);
    return (elapsed / totalTime) * 100;
  };

  if (isExpired) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Your booth hold expired before payment was completed. Your booth
          selection is still reserved for you — start a new payment to finish
          checking out.
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <StyledButton
            variant="contained"
            onClick={onRestart}
            sx={{ minWidth: '200px' }}
          >
            Restart payment
          </StyledButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {statusMessage && !error && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {statusMessage}
        </Alert>
      )}

      {timeLeft !== null && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Time remaining to complete payment: {formatTimeLeft(timeLeft)}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={calculateProgress()}
            sx={{
              height: 8,
              borderRadius: 2,
              backgroundColor: timeLeft < 300 ? '#ffebee' : '#e3f2fd',
              '& .MuiLinearProgress-bar': {
                backgroundColor: timeLeft < 300 ? '#f44336' : '#2196f3',
              },
            }}
          />
          {timeLeft < 300 && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 1, display: 'block' }}
            >
              Please complete your payment soon. Your booth reservation will
              expire in less than 5 minutes.
            </Typography>
          )}
        </Box>
      )}

      <PaymentElement />

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <StyledButton
          type="submit"
          variant="contained"
          disabled={!stripe || loading || (timeLeft !== null && timeLeft <= 0)}
          sx={{ minWidth: '200px' }}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            `Pay $${Number(amount).toFixed(2)}`
          )}
        </StyledButton>
      </Box>
    </Box>
  );
};

const PaymentStep = ({ onNext, onBack, boothData, vendorData }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clientSecret, setClientSecret] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [totalAmount, setTotalAmount] = useState(null);

  const initPayment = useCallback(async () => {
    setLoading(true);
    setError('');
    setClientSecret(null);
    setExpiresAt(null);
    setTotalAmount(null);

    try {
      const response = await fetch('/api/vendor/registration-status', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to check vendor status');
      }

      const data = await response.json();

      const boothIds = Array.isArray(boothData)
        ? boothData.map((b) => b.id)
        : [boothData.id];
      const registrations = data.filter((reg) => boothIds.includes(reg.booth_id));

      const unapprovedBooths = registrations.filter(
        (reg) => reg.registration_status !== 'approved'
      );

      if (unapprovedBooths.length > 0) {
        const boothNumbers = unapprovedBooths
          .map((reg) => reg.booth_number)
          .join(', ');
        setError(
          `Payment is not available for booth(s) ${boothNumbers}. Please wait for admin approval.`
        );
        setLoading(false);
        return;
      }

      await setupPaymentIntent();
    } catch (err) {
      console.error('Error checking vendor status:', err);
      setError(
        "We couldn't check whether your booths are approved yet. Refresh the page; if the problem persists, log out and back in."
      );
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boothData]);

  useEffect(() => {
    initPayment();
  }, [initPayment]);

  const setupPaymentIntent = async () => {
    try {
      if (!boothData) {
        throw new Error('Booth data is required for payment');
      }

      const response = await fetch('/api/payment/create-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          boothData: Array.isArray(boothData)
            ? boothData.map((booth) => ({
                id: booth.booth_id || booth.id,
                cell_name: booth.cell_name,
                is_corner: booth.is_corner,
                needs_electricity: booth.needs_electricity,
                registration_id: booth.id,
              }))
            : [
                {
                  id: boothData.booth_id || boothData.id,
                  cell_name: boothData.cell_name,
                  is_corner: boothData.is_corner,
                  needs_electricity: boothData.needs_electricity,
                  registration_id: boothData.id,
                },
              ],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error ||
            "We couldn't start your payment. Refresh the page and try again. If you keep seeing this, contact the festival organizers."
        );
      }

      const {
        clientSecret: secret,
        amount,
        expiresAt: expiry,
      } = await response.json();
      setClientSecret(secret);
      setExpiresAt(expiry);
      setTotalAmount(amount);
    } catch (err) {
      console.error('Error creating payment intent:', err);
      setError(
        err.message ||
          "We couldn't initialize payment. Refresh the page; if the problem persists, log out, back in, and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#1976d2',
    },
  };

  const boothList = Array.isArray(boothData) ? boothData : [boothData];

  return (
    <Container maxWidth="md">
      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, mt: 2, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ mb: 4, fontWeight: 500 }}>
          Payment Information
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Booth Details
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              {boothList.map((booth, index) => (
                <Box
                  key={booth.id}
                  sx={{ mb: index < boothList.length - 1 ? 2 : 0 }}
                >
                  <Typography>Booth Number: {booth.cell_name}</Typography>
                  {booth.is_corner && (
                    <Typography>Booth Type: Corner</Typography>
                  )}
                  {booth.needs_electricity && (
                    <Typography>Includes Electricity</Typography>
                  )}
                </Box>
              ))}
              {totalAmount !== null && (
                <Typography variant="h6" sx={{ mt: 2, color: 'primary.main' }}>
                  Total Amount: ${Number(totalAmount).toFixed(2)}
                </Typography>
              )}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, fontStyle: 'italic' }}
              >
                Your booth selection will be finalized once payment is
                completed. Please note that in rare circumstances, the Gingham
                Tree team may need to adjust booth assignments to optimize the
                overall event experience. If any changes are necessary, you
                will be notified and provided with an equivalent location.
              </Typography>

              <Alert severity="error" sx={{ mt: 3, border: '2px solid #d32f2f' }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 'bold', mb: 1 }}
                >
                  NO REFUNDS - FINAL PAYMENT
                </Typography>
                <Typography variant="body2">
                  <strong>
                    By proceeding with payment, you acknowledge that all booth
                    fees are FINAL and NON-REFUNDABLE.
                  </strong>{' '}
                  No refunds will be issued for any reason, including weather,
                  emergencies, or change of plans. Please ensure you are fully
                  committed before completing this transaction.
                </Typography>
              </Alert>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            {clientSecret && (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance,
                }}
              >
                <PaymentForm
                  onSuccess={onNext}
                  onError={(err) => setError(err)}
                  onRestart={initPayment}
                  amount={totalAmount}
                  expiresAt={expiresAt}
                />
              </Elements>
            )}
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 4,
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <StyledButton
            onClick={onBack}
            fullWidth
            sx={{ maxWidth: { sm: '200px' } }}
          >
            Back
          </StyledButton>
        </Box>
      </Paper>
    </Container>
  );
};

export default PaymentStep;
